import { useQuery } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Button, Text, Skeleton } from '@repletoryreach/sdk-ui';
import { publicApiUrl } from '../../../lib/public-api';
import { useT } from '../../../i18n/I18nProvider';

/**
 * "Continue with Google", and the rest.
 *
 * The list comes from `GET /v1/auth/providers`, so a provider an operator has
 * not filled in under Settings then Integrations renders no button at all. A
 * button that cannot work is worse than no button: it fails after the person
 * has committed to it, on somebody else's domain, with an error they cannot
 * act on.
 *
 * Pressing one is a full-page navigation, not a fetch. The provider will not
 * answer a background request, and the whole redirect chain has to happen in
 * the address bar for the state cookie to survive it.
 */
export function SocialButtons({ returnTo }: { returnTo?: string }) {
  const t = useT();

  const { data, isPending, error } = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => identity.listAuthProviders(),
    // The answer changes only when an operator edits a registry row, and a
    // sign-in page is re-mounted constantly as somebody moves between sign in,
    // sign up and forgot password.
    staleTime: 5 * 60 * 1000,
    retry: 1
  });

  const providers = data?.data ?? [];

  // A settings service that is unreachable must not take the password form
  // down with it, so a failure here renders nothing at all.
  if (error) return null;

  if (isPending) {
    return (
      <Stack gap="xs">
        <Skeleton height={38} />
      </Stack>
    );
  }

  if (providers.length === 0) return null;

  function start(provider: string) {
    window.location.assign(
      identity.socialSignInUrl(publicApiUrl(), provider, { returnTo, domain: 'tenant' })
    );
  }

  return (
    <Stack gap="sm">
      {/* A rule with a word in it, so the two ways in read as alternatives
          rather than as a form followed by some buttons. Styled inline against
          the theme tokens rather than in the shell stylesheet, so the whole of
          this control lives in one file. */}
      <div
        role="separator"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--rr-space-sm, 8px)',
          color: 'var(--rr-color-text-muted)',
          fontSize: 12
        }}
      >
        <span style={{ flex: 1, height: 1, background: 'var(--rr-color-border)' }} aria-hidden />
        <span>{t('auth.social.or')}</span>
        <span style={{ flex: 1, height: 1, background: 'var(--rr-color-border)' }} aria-hidden />
      </div>

      <Stack gap="xs">
        {providers.map((p) => (
          <Button
            key={p.provider}
            variant="default"
            fullWidth
            onClick={() => start(p.provider)}
            leftSection={<ProviderMark provider={p.provider} />}
          >
            {t('auth.social.continueWith', { provider: p.display_name })}
          </Button>
        ))}
      </Stack>

      <Text role="caption" c="var(--rr-color-text-muted)">
        {t('auth.social.hint')}
      </Text>
    </Stack>
  );
}

/**
 * The marks are drawn rather than fetched: the CDN allowlist aside, a sign-in
 * page should not tell three other companies who is looking at it before the
 * person has chosen one.
 */
function ProviderMark({ provider }: { provider: string }) {
  if (provider === 'google') return <GoogleMark />;
  if (provider === 'facebook') return <FacebookMark />;
  if (provider === 'apple') return <AppleMark />;
  return null;
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 009 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 010-3.44V4.95H.96a9 9 0 000 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.9 11.43 0 9 0A9 9 0 00.96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function FacebookMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path fill="#1877F2" d="M18 9a9 9 0 10-10.41 8.89v-6.29H5.31V9h2.28V7.02c0-2.25 1.34-3.5 3.4-3.5.98 0 2.01.18 2.01.18v2.21h-1.13c-1.12 0-1.47.7-1.47 1.41V9h2.5l-.4 2.6h-2.1v6.29A9 9 0 0018 9z" />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="currentColor" aria-hidden>
      <path d="M13.6 9.55c-.02-1.92 1.57-2.84 1.64-2.89-.9-1.3-2.29-1.48-2.78-1.5-1.18-.12-2.31.7-2.91.7-.6 0-1.53-.68-2.51-.67-1.29.02-2.48.75-3.14 1.9-1.34 2.33-.34 5.77.97 7.65.64.92 1.4 1.96 2.4 1.92.96-.04 1.33-.62 2.49-.62 1.16 0 1.49.62 2.51.6 1.04-.02 1.69-.94 2.32-1.87.73-1.07 1.03-2.1 1.05-2.15-.02-.01-2.02-.78-2.04-3.07zM11.7 3.9c.53-.65.89-1.54.79-2.44-.76.03-1.69.51-2.24 1.15-.49.57-.92 1.48-.8 2.35.85.07 1.72-.43 2.25-1.06z" />
    </svg>
  );
}
